import { ChangeEvent } from 'react';
import { FormikProps } from 'formik';

export const formatDate = (
  dateString: string | null | undefined,
  locale = 'en-GB'
): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date
    .toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .toUpperCase();
};

export const formatLastLoggedInDate = (
  dateString: string | Date | null | undefined
): string => {
  if (!dateString) return 'Never Logged In';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Never Logged In';

  const now = new Date();

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  if (isSameDay(date, now)) {
    return `Today at ${timeString}`;
  } else if (isSameDay(date, yesterday)) {
    return `Yesterday at ${timeString}`;
  } else {
    const dateStr = date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${dateStr} at ${timeString}`;
  }
};

export const formatDateYYYYMMDD = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return 'N/A';
  try {
    const dateStr = dateString.split(' ')[0];
    if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return 'N/A';
  }
};

export const dateFormatYYYYMMDD = (
  date: string | null | undefined | Date
): string => {
  if (!date) return 'N/A';
  if (date instanceof String) {
    return formatDateYYYYMMDD(date as string);
  }
  if (date instanceof Date) {
    if (isNaN(date.getTime())) return 'N/A';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return 'N/A';
};

export const formatCurrency = (
  amount: number | null | undefined,
  locale = 'en-US'
): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (
  value: number | null | undefined,
  locale = 'en-US'
): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat(locale).format(value);
};

export const formatNumberWithCommas = (val: string | number) => {
  if (val === null || val === undefined || val === '') return '';
  const [intPart, decPart] = val.toString().split('.');
  const formattedInt = Number(intPart).toLocaleString('en-US');
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

export const handleNumberFieldChange = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  formik: FormikProps<any>,
  fieldName: string
) => {
  const raw = e.target.value.replace(/,/g, '');
  if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
    formik.setFieldValue(fieldName, raw);
  }
};
